import React from 'react';
import { useTheme } from 'styled-components';
import { Calendar, Walking } from '@solent-university/solent-icons';
import useBreakpoint from 'Common/hooks/useBreakpoint';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import { H2, P } from 'Elements/Typography';
import FlexBox from 'Elements/FlexBox';
import ArrowLink, { ArrowLinkProps } from 'Elements/Link/Arrow';
import ChunkyButton, { ArrowButtonChunkyProps } from 'Elements/Button/Arrow/Chunky';
import Wave from 'Elements/Wave';

import Grid, { Col } from 'Components/Grid';
import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import IconListColumns, { IconListColumnsProps } from 'Components/List/IconList/Columns';
import Carousel, { CarouselProps } from 'Components/Carousel';

import HeroBannerImage, { HeroBannerImageProps } from 'Modules/HeroBanner/MakeWaves/Image';
import AccommodationMap, { AccommodationMapProps } from 'Modules/AccommodationMap';
import InfoBar, { InfoBarProps } from 'Modules/InfoBar';
import LinkCards, { LinkCardsProps } from 'Modules/Cards/Link';

import { ReactComponent as facilitiesWave } from 'Public/graphics/waves/accommodation/wave-facilities.svg';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: HeroBannerImageProps;
    subHero: {
        price: string;
        distance: string;
        tour: ArrowLinkProps | undefined;
        viewOnMap: ArrowLinkProps | undefined;
    };
    features: {
        heading: string;
        items: IconListColumnsProps;
    };
    about: {
        heading: string;
        text: React.ReactNode;
    };
    gallery: CarouselProps;
    included: {
        heading: string;
        items: IconListColumnsProps;
    };
    infoBar?: InfoBarProps;
    facilities: {
        heading: string;
        text: React.ReactNode;
    };
    fees: {
        heading: string;
        leftCol: React.ReactNode;
        rightCol: React.ReactNode;
        disclaimer: string;
    };
    accommodationMap: AccommodationMapProps;
    promoCards: LinkCardsProps;
    relatedPages: {
        heading: string;
        buttons: ArrowButtonChunkyProps[];
    };
}

export const AccommodationHeading: React.FC = ({ children }) => (
    <H2 size={{ base: '1.5rem', large: '2rem' }}>{children}</H2>
);

const Residence: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        subHero,
        features,
        about,
        gallery,
        included,
        infoBar,
        facilities,
        fees,
        accommodationMap,
        promoCards,
        relatedPages
    } = props;

    const { breakpoints, colors } = useTheme();
    const isMobile = !useBreakpoint(breakpoints.medium.value);

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <HeroBannerImage {...heroBanner} />
            <div
                style={{
                    background: colors.secondary.tints[1].hex,
                    padding: '1rem 0'
                }}
            >
                <Grid rowGap="1rem">
                    <Col m={6}>
                        <FlexBox gap="0.5rem" alignItems="center">
                            <Calendar style={{ width: '1.5rem', height: '1.5rem' }} />
                            <P
                                size={{ base: '1.25rem', large: '1.5rem' }}
                                spacing={{ base: { margin: '0.25rem' } }}
                            >
                                Starting from <strong>{subHero.price} per week</strong>
                            </P>
                        </FlexBox>
                        <FlexBox gap="0.5rem" alignItems="center">
                            <Walking style={{ width: '0.75rem', height: '1.5rem' }} />
                            <P
                                size={{ base: '1.25rem', large: '1.5rem' }}
                                spacing={{ base: { margin: '0.25rem' } }}
                            >
                                {subHero.distance} to campus
                            </P>
                        </FlexBox>
                    </Col>
                    <Col m={6} align="center" justify={isMobile ? 'flex-start' : 'flex-end'}>
                        <FlexBox gap="2rem">
                            {subHero.tour && <ArrowLink {...subHero.tour} />}
                            {subHero.viewOnMap && <ArrowLink {...subHero.viewOnMap} />}
                        </FlexBox>
                    </Col>
                </Grid>
            </div>
            <Section id="features" paddingBottom={0.5}>
                <Grid>
                    <Col>
                        <AccommodationHeading>{features.heading}</AccommodationHeading>
                        <IconListColumns {...features.items} />
                    </Col>
                </Grid>
            </Section>
            <Section id="intro" paddingBottom={gallery.slides.length > 0 ? 0.5 : 1}>
                <Grid>
                    <Col l={8}>
                        <AccommodationHeading>{about.heading}</AccommodationHeading>
                        {about.text}
                    </Col>
                </Grid>
            </Section>
            {gallery.slides.length > 0 && (
                <Section id="gallery" paddingBottom={2}>
                    <Grid>
                        <Col l={10}>
                            <AccommodationHeading>Gallery</AccommodationHeading>
                            <Carousel {...gallery} />
                        </Col>
                    </Grid>
                </Section>
            )}
            {included.items.items.length > 0 && (
                <Section
                    id="what-is-included"
                    background
                    backgroundColor={colors.secondary.tints[1].hex}
                    paddingTop={1}
                >
                    <Grid>
                        <Col>
                            <AccommodationHeading>{included.heading}</AccommodationHeading>
                            <IconListColumns {...included.items} />
                        </Col>
                    </Grid>
                </Section>
            )}
            {infoBar && <InfoBar {...infoBar} />}
            {facilities.text && (
                <Section id="facilities" paddingTop={1}>
                    {!isMobile && (
                        <Wave
                            svg={{
                                wave: facilitiesWave,
                                position: {
                                    medium: {
                                        top: '-90rem',
                                        right: '-12rem'
                                    },
                                    large: {
                                        top: '-90rem',
                                        right: '-1.5rem'
                                    }
                                }
                            }}
                            top="-3rem"
                            mask="linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0))"
                            overflowHeight={1500}
                            width={670}
                            height={2589}
                        />
                    )}
                    <Grid>
                        <Col l={8}>
                            <AccommodationHeading>{facilities.heading}</AccommodationHeading>
                            {facilities.text}
                        </Col>
                    </Grid>
                </Section>
            )}
            <Section
                id="fees"
                background
                backgroundColor={colors.primary.hex}
                colour={colors.primaryContrast.hex}
                paddingTop={1}
            >
                <Grid>
                    <Col>
                        <AccommodationHeading>{fees.heading}</AccommodationHeading>
                        <Grid>
                            <Col l={4}>{fees.leftCol}</Col>
                            <Col l={[6, 10]}>{fees.rightCol}</Col>
                        </Grid>
                    </Col>
                    <Col l={8}>
                        <P
                            size={{ base: '1.125rem' }}
                            spacing={{ base: { margin: '0', padding: '1rem 0 0' } }}
                        >
                            {fees.disclaimer}
                        </P>
                    </Col>
                </Grid>
            </Section>
            <AccommodationMap {...accommodationMap} />
            <Section
                id="next-steps"
                background
                backgroundColor={colors.secondary.tints[1].hex}
                backgroundStart
                backgroundStartTop="-50%"
                shape="box"
                paddingBottom={0}
            >
                <LinkCards {...promoCards} />
            </Section>
            <Section id="related-pages" paddingTop={1}>
                <Grid>
                    <Col>
                        <AccommodationHeading>{relatedPages.heading}</AccommodationHeading>
                    </Col>
                </Grid>
                <Grid rowGap="1rem">
                    {relatedPages.buttons.map((button, index) => (
                        <Col key={index} m={6}>
                            <ChunkyButton
                                {...button}
                                variant={index === 0 ? 'accentFourTertiary' : 'accentSixTertiary'}
                            />
                        </Col>
                    ))}
                </Grid>
            </Section>
        </Main>
    );
};

export default Residence;
